import { childFirstIdOf, stepOf } from './historyGroups'
import { commentRules } from './correctionDraft'
import { parseIsoInstant } from './timeGrid'

// RevertPlan (story 211, RN10-RN12): which revert buttons the detail offers, what each one sends and why it may be disabled.
//   { primary?, secondary?, note? } with each action `{ id, targetId, cascade, label, blockers, enabled, notes }`.
// The blockers of `revertibility` are orientative (#205 §8.1): they enable the button or not, the answer of revert decides.

const CHILD_FIRST = 'DATA_CORRECTION_REVERT_CHILD_FIRST'

const blockersOf = (detail) => (detail && detail.revertibility && detail.revertibility.blockers) || []
const withoutChildFirst = (blockers) => blockers.filter((blocker) => blocker.message !== CHILD_FIRST)

// The notes of the confirmation (RN11, M:1083): «Before» always, plus the one of the action.
export const NOTE_ROWS_BACK = { key: 'The records will be as in «Before» again.', params: {} }
const NOTE_BOTH = { key: 'Both steps are reverted: first the trim and then the «close now». It is only possible if nobody has clocked in since then.', params: {} }
const NOTE_CLOSE_ONLY = { key: 'The trim is already reverted: the «close now» is reverted.', params: {} }

const action = (id, targetId, cascade, label, blockers, notes) => ({
  id, targetId, cascade, label, blockers, enabled: blockers.length === 0, notes: [NOTE_ROWS_BACK, ...notes]
})

/**
 * The plan for the detail of `{chosen, close, step}` (the loaded details, as `useHistory` holds them) and the permission.
 *   - without DATA_CORRECTION_REQUEST there is nothing: no button and no note of a blocker (D-S9, GATE2-H9);
 *   - a single correction or a step 2 alone: «Revert…» on itself, `cascade: false`, while APPLIED;
 *   - a close (alone or with its trim): «Revert…» on the CLOSE with `cascade: true` while the close is APPLIED; its blockers
 *     are the trim's (if still applied) then the close's without REVERT_CHILD_FIRST (cascade solves it), the order in which
 *     Sun reverts (RN23 of #210); plus «Revert only the trim…» (`cascade: false`) when BOTH are applied (D-S2).
 * `note` is the first blocker of the first disabled action, shown above the buttons (M:1077).
 * `format(ms)` writes the instant of the «only the trim» note.
 */
export const revertPlanOf = ({ chosen, close, step }, canRequest, format) => {
  if (!canRequest || !chosen) return {}
  const kind = stepOf(chosen)
  const theClose = kind === 'CLOSE' ? chosen : close
  const theStep = kind === 'CLOSE' ? step : chosen
  let plan = {}
  if (kind === 'SINGLE' || !theClose) {
    // A single correction, or a step 2 whose close could not be loaded: revert it alone.
    if (chosen.status === 'APPLIED') plan = { primary: action('REVERT', chosen.id, false, 'Revert…', blockersOf(chosen), []) }
  } else if (theClose.status === 'APPLIED') {
    const trimApplied = theStep ? theStep.status === 'APPLIED' : childFirstIdOf(theClose) !== ''
    const blockers = [...(theStep && trimApplied ? blockersOf(theStep) : []), ...withoutChildFirst(blockersOf(theClose))]
    plan = { primary: action('REVERT', theClose.id, true, 'Revert…', blockers, [trimApplied ? NOTE_BOTH : NOTE_CLOSE_ONLY]) }
    if (theStep && trimApplied) {
      const closedAt = format(parseIsoInstant(theStep.window.end).getTime())
      plan.secondary = action('REVERT_TRIM', theStep.id, false, 'Revert only the trim…', blockersOf(theStep), [
        { key: 'Only the trim is reverted: the «close now» stays applied and the record stays closed at {{time}}.', params: { time: closedAt } }
      ])
    }
  }
  const disabled = [plan.primary, plan.secondary].find((candidate) => candidate && !candidate.enabled)
  if (disabled) plan.note = disabled.blockers[0]
  return plan
}

/** The action of the plan by its id ('REVERT' or 'REVERT_TRIM'). */
export const actionOf = (plan, id) => [plan.primary, plan.secondary].find((candidate) => candidate && candidate.id === id)

/**
 * RN11 (RN53 of #202): the reason must be one of the revert reasons and the comment follows its rules (required with
 * `requiresComment`, at most 1 000, trimmed, empty = null).
 */
export const revertDraftState = ({ reasonCode, comment }, reasons) => {
  const reason = reasons.find((candidate) => candidate.code === reasonCode)
  const rules = commentRules(comment || '', Boolean(reason && reason.requiresComment))
  return { reason, comment: rules, valid: Boolean(reason) && rules.valid }
}

/** RN12: the body of POST dataCorrection/{id}/revert. */
export const revertBodyOf = ({ reasonCode, comment }, revertAction) => {
  const sent = commentRules(comment || '', false).sent
  return { reasonCode, comment: sent === '' ? null : sent, cascade: revertAction.cascade }
}
